/// <reference types="office-js" />
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  CalendarAttendee,
  OfficeItem,
  OutlookEmail,
  OutlookSaveEmail,
  EntryType,
  OutlookParentFolder,
  OutlookFolderName,
  EmailPosition,
  OutlookCalendarEvent,
  OutlookCalendarEventSummary,
} from '@maximizer/outlook/shared/domain';
import {
  BehaviorSubject,
  catchError,
  from,
  lastValueFrom,
  map,
  Observable,
  of,
  Subject as RxSubject,
  switchMap,
} from 'rxjs';
import {
  officeAsyncOrThrow,
  tryOfficeAsync,
  tryOfficeWithFallbackAsync,
} from '../../utils/outlook/outlook.util';
import { nullIfBlank } from '../../utils/string.util';
import { toRichTextFragment } from '../../utils/rich-text.util';
import {
  isAllDayCalendarEvent,
  isPrivateSensitivityOrUnknown,
  supportsAppointmentSensitivity,
} from '../../utils/appointment.util';
import { toClientWallTime } from '../../utils/client-time.util';
import {
  mapOfficeRecurrence,
  supportsAppointmentRecurrence,
} from '../../utils/recurrence.util';
import { TranslateService } from '@ngx-translate/core';
import { OutlookMsalService } from './outlook-msal.service';
import { GRAPH_API_URI } from '../../injection-token';
import { InsightsService } from '@maximizer/core/shared/insights';
import {
  ContextService,
  SessionStorageService,
} from '@maximizer/core/shared/data-access';
import { constructCustomEmlContent } from '../../utils/custom-eml.util';
import { OutlookStore } from '../../state';

const COMPOSE_SUBJECT_POLL_INTERVAL_MS = 500;

const SAVED_IN_MAXIMIZER = 'savedInMaximizer';
const SAVED_OTHER_IN_MAXIMIZER = 'savedOtherInMaximizer';
const SAVED_TO_OPPORTUNITY = 'savedToOpportunity';
const SAVED_TO_CASE = 'savedToCase';
const IMMUTABLE_ID_PREFER_HEADER = 'IdType="ImmutableId"';
const OCTET_STREAM_CONTENT_TYPE = 'application/octet-stream';
const ERROR_SAVING_KEYS = 'Error saving keys in Outlook:';

@Injectable()
export class OutlookService {
  private readonly EMAIL_PREFIX_REGEX = /^(re:|fwd?:?)\s*/i;

  customProperties?: Office.CustomProperties;
  private readonly emailSubject = new BehaviorSubject<OutlookEmail[]>([]);
  readonly emailChanges$ = this.emailSubject.asObservable();
  private readonly composeSubjectChangeSubject = new RxSubject<void>();
  readonly composeSubjectChanges$ =
    this.composeSubjectChangeSubject.asObservable();
  private composeSubjectPollingInterval?: number;
  private lastComposeSubject: string | null = null;

  private getMailboxContext(): Office.Mailbox | null {
    if (typeof Office === 'undefined') {
      return null;
    }

    return Office.context?.mailbox ?? null;
  }

  get mailbox(): Office.Mailbox | null {
    return this.getMailboxContext();
  }

  get mailboxItem(): OfficeItem | null {
    return this.getMailboxContext()?.item ?? null;
  }

  async getSubject(removePrefix: boolean = false): Promise<string> {
    let subject = '';
    const item = this.mailboxItem;
    if (!item) return '';
    const composeSubject = item.subject as Office.Subject | undefined;
    if (typeof composeSubject?.getAsync === 'function') {
      subject =
        (await tryOfficeAsync<string>(
          composeSubject.getAsync.bind(composeSubject),
        )) ?? '';
    } else {
      subject = item.subject;
    }
    return removePrefix
      ? subject.replace(this.EMAIL_PREFIX_REGEX, '').trim()
      : subject;
  }

  get storagePartitionKey(): string {
    const mailbox = this.getMailboxContext();

    return (
      (typeof Office === 'undefined' ? null : Office.context?.partitionKey) ??
      mailbox?.userProfile.emailAddress ??
      'mx_default'
    );
  }

  private composeId: string | null = null;

  constructor(
    @Inject(GRAPH_API_URI) private readonly graphApiUri: string,
    private readonly httpClient: HttpClient,
    private readonly msalService: OutlookMsalService,
    private readonly insightsService: InsightsService,
    private readonly contextService: ContextService,
    private readonly outlookStore: OutlookStore,
    private readonly sessionStorage: SessionStorageService,
    private readonly translateService: TranslateService,
  ) {
    const mailbox = this.getMailboxContext();
    if (mailbox) {
      if (!this.contextService.token) return;
      this.initializeService();
    }
  }

  createNewMessage(options: {
    toRecipients?: string[];
    ccRecipients?: string[];
    bccRecipients?: string[];
    subject?: string;
    htmlBody?: string;
  }): boolean {
    const mailbox = this.getMailboxContext();

    if (!mailbox || typeof mailbox.displayNewMessageForm !== 'function') {
      return false;
    }

    const { htmlBody, ...restOptions } = options;
    const formOptions = {
      ...restOptions,
      htmlBody: htmlBody,
    };

    mailbox.displayNewMessageForm(formOptions);
    return true;
  }

  private initializeService() {
    this.updateProperties();
    this.forceClearOnSendHandlerStorage();
  }

  async updateProperties() {
    if (!Office.context.mailbox.item) {
      console.error('Mailbox item is unavailable');
      return;
    }

    const result = await tryOfficeAsync<Office.CustomProperties>(
      Office.context.mailbox.item.loadCustomPropertiesAsync,
    );

    if (!result) {
      console.error('Unable to load custom properties');
      return;
    }

    this.customProperties = result;
  }

  get isCompose() {
    return this.mailboxItem?.getComposeTypeAsync;
  }

  get isCalendarItem(): boolean {
    const item =
      typeof Office === 'undefined' ? null : Office.context?.mailbox?.item;

    if (item?.itemType === 'appointment') {
      return true;
    }
    return false;
  }

  get isCalendarCompose(): boolean {
    const item =
      typeof Office === 'undefined' ? null : Office.context?.mailbox?.item;
    if (item?.itemType !== 'appointment') return false;
    return (
      typeof (item as Office.AppointmentCompose).requiredAttendees?.getAsync ===
      'function'
    );
  }

  // Rejects on a failed Office read so callers can keep their last good
  // snapshot — a failure must not be mistaken for an empty attendee list.
  async getCalendarAttendees(): Promise<CalendarAttendee[]> {
    const item = this.mailboxItem as Office.AppointmentCompose;
    const [required, optional] = await Promise.all([
      officeAsyncOrThrow<Office.EmailAddressDetails[]>(
        item.requiredAttendees.getAsync.bind(item.requiredAttendees),
      ),
      officeAsyncOrThrow<Office.EmailAddressDetails[]>(
        item.optionalAttendees.getAsync.bind(item.optionalAttendees),
      ),
    ]);

    return [
      ...(required ?? []).map((attendee) => ({
        emailAddress: attendee.emailAddress.toLowerCase(),
        displayName: attendee.displayName,
        isRequired: true,
        isInMaximizer: false,
      })),
      ...(optional ?? []).map((attendee) => ({
        emailAddress: attendee.emailAddress.toLowerCase(),
        displayName: attendee.displayName,
        isRequired: false,
        isInMaximizer: false,
      })),
    ];
  }

  getCalendarAttendeesRead(): CalendarAttendee[] {
    const item = this.mailboxItem as Office.AppointmentRead;
    const required: Office.EmailAddressDetails[] = item.requiredAttendees ?? [];
    const optional: Office.EmailAddressDetails[] = item.optionalAttendees ?? [];
    const userEmail =
      Office.context?.mailbox?.userProfile.emailAddress.toLowerCase();

    return [
      ...required.map((attendee) => ({
        emailAddress: attendee.emailAddress.toLowerCase(),
        displayName: attendee.displayName,
        isRequired: true,
        isInMaximizer: false,
      })),
      ...optional.map((attendee) => ({
        emailAddress: attendee.emailAddress.toLowerCase(),
        displayName: attendee.displayName,
        isRequired: false,
        isInMaximizer: false,
      })),
    ].filter((attendee) => attendee.emailAddress !== userEmail);
  }

  /** Subject and dates only: what the saved-appointment lookup needs, without the body read. */
  async getCalendarEventSummary(): Promise<OutlookCalendarEventSummary> {
    this.requireMailboxItem();
    return this.isCalendarCompose
      ? this.getCalendarEventSummaryCompose()
      : this.getCalendarEventSummaryRead();
  }

  async getCalendarEvent(): Promise<OutlookCalendarEvent> {
    this.requireMailboxItem();
    return this.isCalendarCompose
      ? this.getCalendarEventCompose()
      : this.getCalendarEventRead();
  }

  private requireMailboxItem(): void {
    if (!this.mailboxItem) {
      throw new Error('Calendar event read skipped: no mailbox item');
    }
  }

  private getCalendarEventSummaryRead(): OutlookCalendarEventSummary {
    const item = this.mailboxItem as Office.AppointmentRead;
    return this.toCalendarEventSummary(item.subject, item.start, item.end);
  }

  // Office hands out UTC instants; the add-in works in the zone Outlook displays.
  private toCalendarEventSummary(
    subject: string | null | undefined,
    start: Date,
    end: Date,
  ): OutlookCalendarEventSummary {
    const wallStart = toClientWallTime(start);
    const wallEnd = toClientWallTime(end);
    return {
      subject: subject ?? '',
      start: wallStart,
      end: wallEnd,
      isAllDay: isAllDayCalendarEvent(wallStart, wallEnd),
    };
  }

  private async getCalendarEventRead(): Promise<OutlookCalendarEvent> {
    const item = this.mailboxItem as Office.AppointmentRead;
    const summary = this.getCalendarEventSummaryRead();
    const description = await this.getBodyAsync(Office.CoercionType.Html);

    return {
      ...summary,
      isPrivate: isPrivateSensitivityOrUnknown(
        supportsAppointmentSensitivity() ? item.sensitivity : null,
      ),
      location: nullIfBlank(item.location),
      description: toRichTextFragment(description),
      ...mapOfficeRecurrence(
        supportsAppointmentRecurrence() ? item.recurrence : null,
        summary.start,
        summary.end,
      ),
    };
  }

  private async getCalendarEventSummaryCompose(): Promise<OutlookCalendarEventSummary> {
    const item = this.mailboxItem as Office.AppointmentCompose;
    const [subject, start, end] = await Promise.all([
      officeAsyncOrThrow<string>(item.subject.getAsync.bind(item.subject)),
      officeAsyncOrThrow<Date>(item.start.getAsync.bind(item.start)),
      officeAsyncOrThrow<Date>(item.end.getAsync.bind(item.end)),
    ]);
    if (!start || !end) {
      throw new Error('Calendar event read failed: missing start or end');
    }

    return this.toCalendarEventSummary(subject, start, end);
  }

  private async getCalendarEventCompose(): Promise<OutlookCalendarEvent> {
    const item = this.mailboxItem as Office.AppointmentCompose;
    const [summary, location, description, sensitivity, recurrence] =
      await Promise.all([
        this.getCalendarEventSummaryCompose(),
        officeAsyncOrThrow<string>(item.location.getAsync.bind(item.location)),
        this.getBodyAsync(Office.CoercionType.Html),
        this.getCalendarSensitivityCompose(item),
        this.getCalendarRecurrenceCompose(item),
      ]);

    return {
      ...summary,
      isPrivate: isPrivateSensitivityOrUnknown(sensitivity),
      location: nullIfBlank(location),
      description: toRichTextFragment(description),
      ...mapOfficeRecurrence(recurrence, summary.start, summary.end),
    };
  }

  // An unreadable recurrence must not block the save; it degrades to a single appointment.
  private async getCalendarRecurrenceCompose(
    item: Office.AppointmentCompose,
  ): Promise<Office.Recurrence | null> {
    if (!supportsAppointmentRecurrence()) return null;

    return tryOfficeAsync<Office.Recurrence>(
      item.recurrence.getAsync.bind(item.recurrence),
    );
  }

  // An unreadable sensitivity must not block the save.
  private async getCalendarSensitivityCompose(
    item: Office.AppointmentCompose,
  ): Promise<Office.MailboxEnums.AppointmentSensitivityType | null> {
    if (!supportsAppointmentSensitivity()) return null;

    return tryOfficeAsync<Office.MailboxEnums.AppointmentSensitivityType>(
      item.sensitivity.getAsync.bind(item.sensitivity),
    );
  }

  async addCalendarAttendee(
    emailAddress: string,
    displayName: string,
    isRequired: boolean,
  ): Promise<void> {
    const item = this.mailboxItem as Office.AppointmentCompose;
    const recipients = isRequired
      ? item.requiredAttendees
      : item.optionalAttendees;
    await officeAsyncOrThrow<void>(
      recipients.addAsync.bind(recipients),
      false,
      {
        emailAddress,
        displayName,
      },
    );
  }

  addRecipientsChangedHandler(handler: () => void): Promise<void> {
    const item = this.mailboxItem as Office.AppointmentCompose;
    if (!item) {
      return Promise.reject(
        new Error('RecipientsChanged registration skipped: no mailbox item'),
      );
    }
    return new Promise((resolve, reject) => {
      item.addHandlerAsync(
        Office.EventType.RecipientsChanged,
        handler,
        (result) =>
          result.status === Office.AsyncResultStatus.Succeeded
            ? resolve()
            : reject(
                new Error(
                  `Office addHandlerAsync (${result.error?.code}): ${result.error?.message}`,
                ),
              ),
      );
    });
  }

  removeRecipientsChangedHandler(): void {
    const item = this.mailboxItem as Office.AppointmentCompose;
    item?.removeHandlerAsync(Office.EventType.RecipientsChanged);
  }

  async removeCalendarAttendee(
    emailAddress: string,
    isRequired: boolean,
  ): Promise<void> {
    const item = this.mailboxItem as Office.AppointmentCompose;
    const recipients = isRequired
      ? item.requiredAttendees
      : item.optionalAttendees;

    const current = await officeAsyncOrThrow<Office.EmailAddressDetails[]>(
      recipients.getAsync.bind(recipients),
      true,
      null,
    );

    const updated = (current ?? []).filter(
      (r) => r.emailAddress?.toLowerCase() !== emailAddress.toLowerCase(),
    );

    await officeAsyncOrThrow<void>(
      recipients.setAsync.bind(recipients),
      true,
      updated,
    );
  }

  isOutlookLegacyDesktop(): boolean {
    return Office.context.platform === Office.PlatformType.PC;
  }

  get platformLabel(): string {
    if (typeof Office === 'undefined') return '[Unknown]';

    const platform = Office.context?.platform;
    const hostName = Office.context?.mailbox?.diagnostics?.hostName;

    switch (hostName) {
      case 'newOutlookWindows':
        return 'Outlook Desktop';
      case 'OutlookWebApp':
        return 'Outlook Browser App';
      case 'OutlookIOS':
        return 'Outlook for iOS';
      case 'OutlookAndroid':
        return 'Outlook for Android';
      case 'Outlook':
        // Classic Outlook on Windows and any Outlook on Mac both report
        // 'Outlook'; only `platform` separates them.
        return platform === Office.PlatformType.Mac
          ? 'Outlook Desktop (Mac)'
          : 'Outlook Classic';
    }

    // Fallback for clients that don't expose `diagnostics.hostName`.
    if (!platform) return '[Unknown]';
    if (platform === Office.PlatformType.PC) return 'Outlook Classic';
    if (platform === Office.PlatformType.Mac) return 'Outlook Desktop (Mac)';
    if (platform === Office.PlatformType.OfficeOnline)
      return 'Outlook Browser App';
    return platform as unknown as string;
  }

  async updateEmails(): Promise<void> {
    const item = this.mailboxItem;
    if (!item) return;

    if (this.isCompose) {
      await this.getComposeEmails(item);
    } else {
      const newEmails = this.getReadEmails();
      this.emailSubject.next(Array.from(newEmails));
    }
  }

  async startComposeSubjectChangeTracking(): Promise<void> {
    if (!this.isCompose || this.composeSubjectPollingInterval) return;

    try {
      this.lastComposeSubject = await this.getSubject();
    } catch {
      this.lastComposeSubject = null;
    }
    this.scheduleNextSubjectPoll();
  }

  stopComposeSubjectChangeTracking(): void {
    if (this.composeSubjectPollingInterval) {
      clearTimeout(this.composeSubjectPollingInterval);
      this.composeSubjectPollingInterval = undefined;
    }

    this.lastComposeSubject = null;
  }

  private scheduleNextSubjectPoll(): void {
    this.composeSubjectPollingInterval = window.setTimeout(async () => {
      await this.detectComposeSubjectChange();
      if (this.composeSubjectPollingInterval) {
        this.scheduleNextSubjectPoll();
      }
    }, COMPOSE_SUBJECT_POLL_INTERVAL_MS);
  }

  private async detectComposeSubjectChange(): Promise<void> {
    try {
      if (!this.isCompose) return;

      const subject = await this.getSubject();
      if (this.lastComposeSubject === null) {
        this.lastComposeSubject = subject;
        return;
      }

      if (subject === this.lastComposeSubject) return;

      this.lastComposeSubject = subject;
      this.composeSubjectChangeSubject.next();
    } catch {
      // Swallow Office.js errors to keep polling alive.
    }
  }

  getReadEmails(): Office.EmailAddressDetails[] {
    const item = Office.context.mailbox.item;
    if (!item) return [];
    const newEmails: Office.EmailAddressDetails[] = [];
    const officeEmail =
      Office.context.mailbox?.userProfile.emailAddress.toLocaleLowerCase();

    const from = this.getSenderEmail(item);
    if (from) {
      this.addEmailsToNewList([from], newEmails, officeEmail);
    }

    this.addEmailsToNewList(item.to, newEmails, officeEmail);
    this.addEmailsToNewList(item.cc, newEmails, officeEmail);
    const bcc = (item.bcc as unknown as Office.EmailAddressDetails[]) || [];
    this.addEmailsToNewList(bcc, newEmails, officeEmail);

    return newEmails;
  }

  getReadReplyRecipients(): { to: string[]; cc: string[] } {
    const item = Office.context.mailbox.item;
    if (!item) return { to: [], cc: [] };

    const officeEmail =
      Office.context.mailbox?.userProfile.emailAddress.toLocaleLowerCase() ??
      '';

    const toDetails: Office.EmailAddressDetails[] = [];
    const sender = this.getSenderEmail(item);
    if (sender) this.addEmailsToNewList([sender], toDetails, officeEmail);
    this.addEmailsToNewList(item.to, toDetails, officeEmail);

    const ccDetails: Office.EmailAddressDetails[] = [];
    this.addEmailsToNewList(item.cc, ccDetails, officeEmail);

    const toAddresses = toDetails.map((detail) => detail.emailAddress);
    const toLookup = new Set(
      toAddresses.map((address) => address.toLocaleLowerCase()),
    );
    const ccAddresses = ccDetails
      .map((detail) => detail.emailAddress)
      .filter((address) => !toLookup.has(address.toLocaleLowerCase()));

    return { to: toAddresses, cc: ccAddresses };
  }

  async getParticipantEmails(): Promise<Office.EmailAddressDetails[]> {
    const item = this.mailboxItem;
    if (!item) return [];
    return this.isCompose
      ? this.getComposeParticipantEmails(item)
      : this.getReadEmails();
  }

  private async getComposeParticipantEmails(
    item: OfficeItem,
  ): Promise<Office.EmailAddressDetails[]> {
    if (!item) return [];
    const officeEmail = (await this.getMailboxEmail()).toLocaleLowerCase();
    const result: Office.EmailAddressDetails[] = [];
    for (const field of [item.to, item.cc, item.bcc]) {
      const emails = await tryOfficeAsync<Office.EmailAddressDetails[]>(
        field.getAsync,
        false,
      );
      this.addEmailsToNewList(emails ?? [], result, officeEmail);
    }
    return result;
  }

  private addEmailsToNewList(
    emails: Office.EmailAddressDetails[],
    newEmails: Office.EmailAddressDetails[],
    officeEmail: string,
  ) {
    if (!emails?.length) return;

    for (const email of emails) {
      if (!email?.emailAddress) continue;

      const emailEntryLower = email.emailAddress.toLocaleLowerCase();
      if (emailEntryLower.localeCompare(officeEmail) === 0) continue;
      if (
        newEmails.some(
          (existingEmail) =>
            existingEmail.emailAddress.toLocaleLowerCase() === emailEntryLower,
        )
      )
        continue;

      newEmails.push(email);
    }
  }

  getSenderEmail(item: OfficeItem): Office.EmailAddressDetails | null {
    if (!item) return null;

    const from = item.from;
    const result = item.from as Office.EmailAddressDetails;

    if (from) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result.emailAddress = from.emailAddress || (from as any).address;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result.displayName = from.displayName || (from as any).name;
      return result;
    }
    return null;
  }

  getGraphEmailId(itemId: string): string {
    if (!itemId) return '';

    const mailbox = this.getMailboxContext();
    if (!mailbox) return '';

    return mailbox.convertToRestId(
      itemId,
      Office.MailboxEnums.RestVersion.v2_0,
    );
  }

  private async buildEmlFileFromItemId(
    itemId: string,
    subject: string,
    removeAttachment: boolean,
  ): Promise<File> {
    const restItemId = this.getGraphEmailId(itemId);
    let emlContent = '';
    const contentStr = await this.getMimeContent(restItemId, removeAttachment);
    if (!contentStr) {
      throw new Error('Failed to retrieve email content');
    }

    emlContent = removeAttachment
      ? constructCustomEmlContent(JSON.parse(contentStr))
      : contentStr;

    const emailName = subject.replaceAll('"', '_').trim() + '.eml';
    const blob = new Blob([emlContent], { type: OCTET_STREAM_CONTENT_TYPE });

    return new File([blob], emailName, {
      type: OCTET_STREAM_CONTENT_TYPE,
    });
  }

  async getReadEmlFile(removeAttachment = false): Promise<File | null> {
    try {
      const itemId = this.mailboxItem?.itemId;
      const subject =
        nullIfBlank(this.mailboxItem?.subject) ??
        this.translateService.instant('outlook.email.noSubject');

      if (!itemId) {
        throw new Error('Failed to retrieve itemId on EML file retrieval');
      }

      return await this.buildEmlFileFromItemId(
        itemId,
        subject,
        removeAttachment,
      );
    } catch (error) {
      console.error('Error in getReadEmlFile:', error);
      throw error;
    }
  }

  async getComposeEmlFile(
    itemId: string | undefined = undefined,
    removeAttachment = false,
  ): Promise<File | null> {
    try {
      itemId ??= this.mailboxItem?.itemId;

      if (!itemId || !this.mailboxItem) {
        throw new Error('Failed to retrieve itemId on EML file retrieval');
      }

      const subject =
        nullIfBlank(
          await tryOfficeAsync<string>(
            this.mailboxItem.subject.getAsync,
            false,
          ),
        ) ?? this.translateService.instant('outlook.email.noSubject');

      return await this.buildEmlFileFromItemId(
        itemId,
        subject,
        removeAttachment,
      );
    } catch (error) {
      console.error('Error in getComposeEmlFile:', error);
      throw error;
    }
  }

  // Save Now: the draft must be committed before Graph can return its MIME content
  async getSavedComposeEmlFile(removeAttachment = false): Promise<File | null> {
    const item = this.mailboxItem;
    if (!item) return null;

    const itemId = await this.getComposeEmailId();
    if (!itemId) return null;

    const saveResult = await tryOfficeAsync<string>(item.saveAsync, false);
    if (saveResult === null) return null;

    return await this.getComposeEmlFile(itemId, removeAttachment);
  }

  private async getMimeContent(
    itemId: string,
    removeAttachment = false,
  ): Promise<string | null> {
    try {
      const { accessToken } = await this.msalService.getMsalAuthentication();

      const getMessageUrl = removeAttachment
        ? await this.getMimeContentUrl(itemId, '', true)
        : await this.getMimeContentUrl(itemId, '$value', false);

      return await lastValueFrom(
        this.httpClient.get(getMessageUrl, {
          headers: new HttpHeaders({
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': removeAttachment
              ? 'application/json'
              : 'message/rfc822',
            Prefer: IMMUTABLE_ID_PREFER_HEADER,
          }),
          responseType: 'text',
        }),
      );
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Unknown MIME content error');
      const authRelatedError = this.isMimeAuthRelatedError(err);
      console.warn('Failed to get MIME content:', error.message);
      this.insightsService.trackException(
        { error },
        {
          component: 'OutlookService',
          event: 'outlook-service-mime-content-failed',
          authRelatedError,
        },
      );
      return null;
    }
  }

  private async getMimeContentUrl(
    itemId: string,
    searchFor: string,
    removeAttachment = false,
  ): Promise<string> {
    const isShared = this.outlookStore.isSharedMailbox();
    const basePath = isShared
      ? `${this.graphApiUri}/users/${await this.getMailboxEmail()}/messages/${itemId}`
      : `${this.graphApiUri}/me/messages/${itemId}`;

    // appending /$value returns mime content, base path returns JSON for custom EML
    if (!removeAttachment) {
      return `${basePath}/${searchFor}`;
    }
    return basePath;
  }

  async getInternetMessageId(itemId: string): Promise<string | null> {
    try {
      if (this.isOutlookLegacyDesktop() && this.isCompose) {
        return null;
      }

      const restItemId = this.getGraphEmailId(itemId);

      const { accessToken } = await this.msalService.getMsalAuthentication();

      const getMessageUrl = await this.getMimeContentUrl(
        restItemId,
        '?$select=internetMessageId',
      );
      const response = await lastValueFrom(
        this.httpClient.get<{ internetMessageId?: string }>(getMessageUrl, {
          headers: new HttpHeaders({
            Authorization: `Bearer ${accessToken}`,
            Prefer: IMMUTABLE_ID_PREFER_HEADER,
          }),
          responseType: 'json',
        }),
      );

      if (!response?.internetMessageId) {
        console.warn('internetMessageId not found in response');
        return null;
      }

      return response.internetMessageId;
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Unknown internetMessageId error');
      console.error(error, 'Failed to get internetMessageId:', error.message);
      this.insightsService.trackException({ error });
      return null;
    }
  }

  async saveKeysInOutlook(entries: OutlookEmail[], isOtherEntries: boolean) {
    if (!this.customProperties) {
      await this.updateProperties();
      if (!this.customProperties) return;
    }

    let savedEmails = await this.getSavedEmails();
    if (!savedEmails) {
      savedEmails = {
        othersInMaximizer: [] as OutlookEmail[],
        inMaximizer: [] as OutlookEmail[],
        opportunities: [],
        cases: [],
      };
    }

    entries.forEach((entry) => {
      const exists = isOtherEntries
        ? savedEmails.othersInMaximizer.some((e) => e.id === entry.id)
        : savedEmails.inMaximizer.some((e) => e.id === entry.id);

      if (!exists) {
        if (isOtherEntries) {
          savedEmails.othersInMaximizer.push(entry);
        } else {
          savedEmails.inMaximizer.push(entry);
        }
      }
    });

    try {
      const toStoredEmailReference = (emails: OutlookEmail[]) =>
        emails.map((email) => ({ id: email.id }));

      this.customProperties.remove('savedEmails');
      this.customProperties.set(
        SAVED_IN_MAXIMIZER,
        JSON.stringify(toStoredEmailReference(savedEmails.inMaximizer)),
      );
      this.customProperties.set(
        SAVED_OTHER_IN_MAXIMIZER,
        JSON.stringify(toStoredEmailReference(savedEmails.othersInMaximizer)),
      );

      this.customProperties.saveAsync();
    } catch (error) {
      console.error(ERROR_SAVING_KEYS, error);
    }
  }

  async saveOpportunityOrCaseInOutlook(key: string, type: EntryType) {
    if (!this.customProperties) {
      await this.updateProperties();
      if (!this.customProperties) return;
    }

    let savedEmails = await this.getSavedEmails();
    if (!savedEmails) {
      savedEmails = {
        othersInMaximizer: [],
        inMaximizer: [],
        opportunities: [],
        cases: [],
      };
    }

    if (type === 'opportunity') {
      if (savedEmails.opportunities.includes(key)) {
        return;
      }
      savedEmails.opportunities.push(key);
    } else if (type === 'case') {
      if (savedEmails.cases.includes(key)) {
        return;
      }
      savedEmails.cases.push(key);
    }

    try {
      const savedEmailInOpportunity = JSON.stringify(savedEmails.opportunities);
      const savedEmailInCase = JSON.stringify(savedEmails.cases);

      this.customProperties.set(SAVED_TO_OPPORTUNITY, savedEmailInOpportunity);
      this.customProperties.set(SAVED_TO_CASE, savedEmailInCase);
      this.customProperties.saveAsync();
    } catch (error) {
      console.error(ERROR_SAVING_KEYS, error);
    }
  }

  async getSavedEmails(retry = false): Promise<OutlookSaveEmail> {
    const result: OutlookSaveEmail = {
      othersInMaximizer: [],
      inMaximizer: [],
      opportunities: [],
      cases: [],
    };
    try {
      await this.updateProperties();

      result.inMaximizer = this.getEmailsFromOutlookByKey(
        SAVED_IN_MAXIMIZER,
      ) as OutlookEmail[];
      result.othersInMaximizer = this.getEmailsFromOutlookByKey(
        SAVED_OTHER_IN_MAXIMIZER,
      ) as OutlookEmail[];
      result.opportunities = this.getEmailsFromOutlookByKey(
        SAVED_TO_OPPORTUNITY,
      ) as string[];
      result.cases = this.getEmailsFromOutlookByKey(SAVED_TO_CASE) as string[];
    } catch (error) {
      if (retry) {
        this.insightsService.trackException(
          { error: error instanceof Error ? error : new Error(String(error)) },
          { event: 'outlook-service-get-saved-emails-failed' },
        );
        return result;
      }
      return await this.getSavedEmails(true);
    }
    return result;
  }

  async checkSavedEmailByKey(parentKey: string): Promise<boolean> {
    try {
      await this.updateProperties();

      const inMaximizer =
        (this.getEmailsFromOutlookByKey(SAVED_IN_MAXIMIZER) as {
          id: string;
        }[]) || [];
      const othersInMaximizer =
        (this.getEmailsFromOutlookByKey(SAVED_OTHER_IN_MAXIMIZER) as {
          id: string;
        }[]) || [];
      const opportunities =
        (this.getEmailsFromOutlookByKey(SAVED_TO_OPPORTUNITY) as string[]) ||
        [];
      const cases =
        (this.getEmailsFromOutlookByKey(SAVED_TO_CASE) as string[]) || [];

      const existInMax = inMaximizer.some((o) => o.id === parentKey);
      const notInMax = othersInMaximizer.some((o) => o.id === parentKey);
      const oppInMax = opportunities.includes(parentKey);
      const caseInMax = cases.includes(parentKey);

      return existInMax || notInMax || oppInMax || caseInMax;
    } catch (error) {
      console.error(
        'Error in checkSavedEmailByKey method for parentKey:',
        parentKey,
        error,
      );
      return false;
    }
  }

  // Probes for an existing item without creating one; getComposeEmailId would save the item instead.
  async getCalendarItemId(): Promise<string | null> {
    if (!this.mailboxItem) return null;

    if (!this.isCalendarCompose) {
      const item = this.mailboxItem as Office.AppointmentRead;
      return item.itemId ?? null;
    }

    const item = this.mailboxItem as Office.AppointmentCompose;
    return await tryOfficeAsync<string>(item.getItemIdAsync.bind(item));
  }

  private getEmailsFromOutlookByKey(key: string): OutlookEmail[] | string[] {
    try {
      if (!this.customProperties) return [];
      const result = this.customProperties.get(key);

      if (!result) {
        return [];
      }
      if (typeof result == 'object') return result;

      return JSON.parse(result);
    } catch (error) {
      console.error('Error GetEmailsFromOutlookByKey', key, error);
      return [];
    }
  }

  // ---------- COMPOSE Methods ----------

  async getComposeEmails(item: OfficeItem): Promise<void> {
    if (!item) return;
    const userOfficeEmail = await this.getMailboxEmail();

    let emails: OutlookEmail[] = [];

    const to = await tryOfficeAsync<Office.EmailAddressDetails[]>(
      item.to.getAsync,
      false,
    );
    if (to?.length) {
      for (const email of to) {
        emails = this.addEmailToComposeList(
          'to',
          email,
          emails,
          userOfficeEmail,
        );
      }
    }

    const cc = await tryOfficeAsync<Office.EmailAddressDetails[]>(
      item.cc.getAsync,
      false,
    );
    if (cc?.length) {
      for (const email of cc) {
        emails = this.addEmailToComposeList(
          'cc',
          email,
          emails,
          userOfficeEmail,
        );
      }
    }

    const bcc = await tryOfficeAsync<Office.EmailAddressDetails[]>(
      item.bcc.getAsync,
      false,
    );
    if (bcc?.length) {
      for (const email of bcc) {
        emails = this.addEmailToComposeList(
          'bcc',
          email,
          emails,
          userOfficeEmail,
        );
      }
    }

    const saveOnly = this.sessionStorage.get('saveOnlyEntries');
    const saveOnlyParsed = saveOnly ? JSON.parse(saveOnly) : [];
    if (saveOnlyParsed?.length > 0) {
      for (const email of saveOnlyParsed) {
        emails = this.addEmailToComposeList(
          'saveOnly',
          email,
          emails,
          userOfficeEmail,
        );
      }
    }

    this.emailSubject.next(emails);
  }

  private addEmailToComposeList(
    position: EmailPosition,
    outlook: OutlookEmail,
    emails: OutlookEmail[],
    userOfficeEmail: string,
  ): OutlookEmail[] {
    if (outlook.emailAddress.toLowerCase() === userOfficeEmail) {
      return [...emails];
    }

    const updatedOutlook: OutlookEmail = {
      ...outlook,
      position,
      localId: outlook.emailAddress + emails.length,
      emailAddress: outlook.emailAddress.toLowerCase(),
    };

    return [...emails, updatedOutlook];
  }

  async addEmailToPosition(
    email: OutlookEmail,
    position: EmailPosition,
  ): Promise<void> {
    const item = Office.context.mailbox.item;
    const finalEmail = email as Office.EmailAddressDetails;
    if (!finalEmail || !item) {
      throw new Error('Cannot add recipient: no compose mailbox item');
    }

    switch (position) {
      case 'to':
        await officeAsyncOrThrow<void>(item.to.addAsync, false, finalEmail);
        break;
      case 'cc':
        await officeAsyncOrThrow<void>(item.cc.addAsync, false, finalEmail);
        break;
      case 'bcc':
        await officeAsyncOrThrow<void>(item.bcc.addAsync, false, finalEmail);
        break;
      default:
        throw new Error(`Cannot add recipient to position "${position}"`);
    }
  }

  async removeEmailFromPosition(
    email: OutlookEmail,
    position: EmailPosition,
  ): Promise<void> {
    const item = Office.context.mailbox.item;
    const finalEmail = email as Office.EmailAddressDetails;
    if (!finalEmail || !item) {
      throw new Error('Cannot remove recipient: no compose mailbox item');
    }

    let recipients: Office.Recipients | null = null;
    if (position === 'to') {
      recipients = item.to;
    } else if (position === 'cc') {
      recipients = item.cc;
    } else if (position === 'bcc') {
      recipients = item.bcc;
    }

    if (!recipients) {
      throw new Error(`Cannot remove recipient from position "${position}"`);
    }

    const currentRecipients = await officeAsyncOrThrow<
      Office.EmailAddressDetails[]
    >(recipients.getAsync.bind(recipients), true, null);

    const updatedRecipients = (currentRecipients ?? []).filter(
      (recipient) =>
        recipient.emailAddress?.toLowerCase() !==
        finalEmail.emailAddress?.toLowerCase(),
    );

    await officeAsyncOrThrow<void>(
      recipients.setAsync.bind(recipients),
      true,
      updatedRecipients,
    );
  }

  async moveEmailToPosition(
    email: OutlookEmail,
    from: EmailPosition,
    to: EmailPosition,
  ): Promise<void> {
    await this.addEmailToPosition(email, to);
    await this.removeEmailFromPosition(email, from);
  }

  async getComposeEmailId(retry: boolean = false): Promise<string | null> {
    if (!this.mailboxItem) {
      return null;
    }
    if (this.composeId) {
      return this.composeId;
    }

    if (this.mailboxItem.itemId) {
      this.composeId = this.mailboxItem.itemId;
    } else {
      this.composeId = await tryOfficeWithFallbackAsync<string>(
        this.mailboxItem.getItemIdAsync,
        this.mailboxItem.saveAsync,
      );
      if (!this.composeId && !retry) {
        this.getComposeEmailId(true);
      }
    }
    return this.composeId;
  }

  // Track inactivity, office.js doesn't provide onclose/onsend for Taskpane
  private static heartbeatInterval?: number;
  private static heartbeatOnStop?: () => void;
  private static heartbeatTickInFlight = false;

  async startHeartBeat(onTick: () => Promise<void>, onStop?: () => void) {
    if (OutlookService.heartbeatInterval) return;
    if (this.isOutlookLegacyDesktop()) return;
    if (this.outlookStore.isSharedMailbox()) return;

    OutlookService.heartbeatOnStop = onStop;
    OutlookService.heartbeatInterval = setInterval(async () => {
      if (OutlookService.heartbeatTickInFlight) return;

      OutlookService.heartbeatTickInFlight = true;
      try {
        await onTick();
      } finally {
        OutlookService.heartbeatTickInFlight = false;
      }
    }, 60_000) as unknown as number;
  }

  stopHeartbeat() {
    if (OutlookService.heartbeatInterval) {
      clearInterval(OutlookService.heartbeatInterval);
      OutlookService.heartbeatInterval = undefined;
    }
    OutlookService.heartbeatTickInFlight = false;
    OutlookService.heartbeatOnStop?.();
    OutlookService.heartbeatOnStop = undefined;
  }

  forceClearOnSendHandlerStorage(): void {
    try {
      const keysToRemove: string[] = [];

      for (let i = 0; i < globalThis.localStorage.length; i++) {
        const key = globalThis.localStorage.key(i);
        if (!key) continue;

        if (
          key.startsWith('Mx-Id:') ||
          key.startsWith('Mx-Api:') ||
          key.startsWith('Mx-Keys:') ||
          key === 'Mx-TaskpaneIsOpened' ||
          key === 'Mx-RefreshTaskpane' ||
          key === 'Mx-OnSendErrorsTaskpane'
        ) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => globalThis.localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear OnSend handler storage:', error);
    }
  }

  async getMailboxEmail(): Promise<string> {
    const userEmail =
      this.mailbox?.userProfile.emailAddress.toLocaleLowerCase() ?? '';
    const mailboxItem =
      typeof Office === 'undefined'
        ? null
        : (Office.context?.mailbox?.item ?? null);

    if (!userEmail || !mailboxItem?.getSharedPropertiesAsync) return userEmail;

    const result = await tryOfficeAsync<Office.SharedProperties>(
      mailboxItem.getSharedPropertiesAsync,
      false,
    );

    return result ? result.owner.toLocaleLowerCase() : userEmail;
  }

  async isSharedMailbox(): Promise<boolean> {
    try {
      const { item } = Office.context.mailbox;
      if (
        this.isCompose &&
        Office.context.platform !== Office.PlatformType.Mac
      ) {
        const userEmail =
          this.mailbox?.userProfile.emailAddress.toLocaleLowerCase() ?? '';
        const owner = await this.getMailboxEmail();
        const from = await this.getComposeFrom();

        // Check the From address too, not just the mailbox owner: a draft
        // started in a shared folder lets you switch "From" to another
        // address (send-as) — a shared scenario even in your own mailbox.
        const composingInSharedMailbox = owner !== userEmail;
        const sendingAsAnotherAddress = from !== userEmail;

        return composingInSharedMailbox || sendingAsAnotherAddress;
      }
      if (!item?.getSharedPropertiesAsync) return false;

      const result = await tryOfficeAsync<Office.SharedProperties>(
        item.getSharedPropertiesAsync,
        false,
      );

      return !!result?.owner;
    } catch (error) {
      this.insightsService.trackException({ error: error as Error });
      return false;
    }
  }

  async getComposeFrom(): Promise<string> {
    const from = (this.mailboxItem as Office.MessageCompose | null)?.from;
    if (typeof from?.getAsync !== 'function') return '';
    const fromMailbox = await tryOfficeAsync<Office.EmailAddressDetails>(
      from.getAsync.bind(from),
      false,
    );
    return fromMailbox?.emailAddress.toLocaleLowerCase() ?? '';
  }

  async getEmailParentFolderId(): Promise<string | null> {
    try {
      if (this.isCompose) return null; // Compose items don't have a parent folder
      if (this.isCalendarItem) return null; // Appointments live on me/calendar/events, not /me/messages

      const itemId = this.mailboxItem?.itemId;
      if (!itemId) return null;

      const restItemId = this.getGraphEmailId(itemId);

      const { accessToken } = await this.msalService.getMsalAuthentication();

      if (!accessToken) {
        console.warn('No access token available for folder lookup');
        return null;
      }

      const messageResponse = await lastValueFrom(
        this.httpClient.get<{ parentFolderId: string }>(
          `${this.graphApiUri}/me/messages/${encodeURIComponent(restItemId)}?$select=parentFolderId`,
          {
            headers: new HttpHeaders({
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
              Prefer: IMMUTABLE_ID_PREFER_HEADER,
            }),
          },
        ),
      );

      if (!messageResponse?.parentFolderId) {
        console.warn('No parent folder ID found in response');
        return null;
      }

      return messageResponse.parentFolderId;
    } catch (error) {
      console.warn('Error getting parent folder type:', error);
      return null;
    }
  }

  async getEmailParentFolderDetails(): Promise<OutlookParentFolder | null> {
    try {
      const parentFolderId = await this.getEmailParentFolderId();
      if (!parentFolderId) return null;

      const { accessToken } = await this.msalService.getMsalAuthentication();

      if (!accessToken) {
        console.warn('No access token available for folder details lookup');
        return null;
      }

      const folderResponse = await lastValueFrom(
        this.httpClient.get<OutlookParentFolder>(
          `${this.graphApiUri}/me/mailFolders/${encodeURIComponent(parentFolderId)}`,
          {
            headers: new HttpHeaders({
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            }),
          },
        ),
      );

      return folderResponse;
    } catch (error) {
      console.warn('Error getting parent folder details:', error);
      this.insightsService.trackException({ error: error as Error });
      return null;
    }
  }

  async isSentFolder(): Promise<boolean> {
    const parentFolder = await this.getEmailParentFolderDetails();

    return parentFolder?.displayName === OutlookFolderName.SentItems;
  }

  async getEmailBodyAsync(): Promise<string> {
    return this.getBodyAsync(Office.CoercionType.Text);
  }

  private async getBodyAsync(
    coercionType: Office.CoercionType,
  ): Promise<string> {
    if (!this.mailboxItem) return '';
    return (
      (await tryOfficeAsync(
        this.mailboxItem.body.getAsync.bind(this.mailboxItem.body),
        true,
        coercionType,
      )) ?? ''
    );
  }

  async prependToEmailBodyAsync(html: string): Promise<boolean> {
    const item = this.mailboxItem;
    if (!item) return false;
    return new Promise<boolean>((resolve) => {
      item.body.prependAsync(
        html,
        { coercionType: Office.CoercionType.Html },
        (result) =>
          resolve(result.status === Office.AsyncResultStatus.Succeeded),
      );
    });
  }

  getMessageBodyText(): Observable<string> {
    if (this.isCompose) {
      return from(this.getEmailBodyAsync());
    }
    const messageId = this.mailboxItem?.itemId ?? '';
    return this.getGraphMessageBodyText(messageId).pipe(
      map((body) => body ?? ''),
    );
  }

  getGraphMessageBodyText(messageId: string): Observable<string | null> {
    if (!messageId) {
      return of(null);
    }

    const graphMessageId = this.getGraphEmailId(messageId);

    return from(this.msalService.getMsalAuthentication()).pipe(
      switchMap(({ accessToken }) => {
        if (!accessToken) {
          console.warn('No access token available for message body lookup');
          return of(null);
        }

        return this.httpClient
          .get<{ body?: { content?: string } }>(
            `${this.graphApiUri}/me/messages/${encodeURIComponent(graphMessageId)}?$select=body`,
            {
              headers: new HttpHeaders({
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                Prefer: 'outlook.body-content-type="text"',
              }),
            },
          )
          .pipe(map((response) => response?.body?.content ?? null));
      }),
      catchError((error) => {
        console.warn('Error getting message body:', error);
        this.insightsService.trackException({ error: error as Error });
        return of(null);
      }),
    );
  }

  private isMimeAuthRelatedError(error: unknown): boolean {
    const record = this.asRecord(error);
    const nestedError = this.asRecord(record?.['error']);
    const status = record?.['status'];

    if (status === 401 || status === 403) {
      return true;
    }

    const description = [
      error instanceof Error ? error.message : '',
      typeof record?.['message'] === 'string' ? record['message'] : '',
      typeof record?.['error'] === 'string' ? record['error'] : '',
      typeof nestedError?.['message'] === 'string'
        ? nestedError['message']
        : '',
      typeof nestedError?.['error_description'] === 'string'
        ? nestedError['error_description']
        : '',
      typeof nestedError?.['error'] === 'string' ? nestedError['error'] : '',
    ]
      .join(' ')
      .toLowerCase();

    return (
      description.includes('interactionrequiredautherror') ||
      description.includes('no active account') ||
      description.includes('invalid_grant') ||
      description.includes('unauthorized') ||
      description.includes('consent')
    );
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : null;
  }
}
